import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon, Send as SendIcon } from '@mui/icons-material';
import axios from 'axios';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/** Business / franchisee dashboard: citizen requirements inbox + send offers */
const CitizenRequirementsInbox = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [offerForm, setOfferForm] = useState({
    offer_price_label: '',
    available_timing: '',
    message: '',
  });
  const [sending, setSending] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/citizen-postings/provider/inbox`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.data?.success) {
        setRows(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const sendOffer = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/citizen-postings/provider/inbox/${selected.id}/offers`,
        offerForm,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setSelected(null);
      setOfferForm({ offer_price_label: '', available_timing: '', message: '' });
      loadRows();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send offer');
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Citizen Requirements Inbox
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Posts matched to your business categories. Send price & timing offers.
            </Typography>
          </Box>
          <Button startIcon={<RefreshIcon />} onClick={loadRows} disabled={loading}>
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Requirement</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell align="right">Offer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{row.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.description?.slice(0, 80)}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.category_label || row.category_key}</TableCell>
                    <TableCell>{row.location_name || '—'}</TableCell>
                    <TableCell>
                      {row.user_phone ? (
                        row.user_phone
                      ) : (
                        <Chip size="small" label="In-app offer only" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SendIcon />}
                        onClick={() => setSelected(row)}
                      >
                        Reply
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      No matching requirements right now.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Send offer</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {selected?.title}
              </Typography>
              <TextField
                label="Price / quote"
                placeholder="e.g. ₹500 visit + parts"
                value={offerForm.offer_price_label}
                onChange={(e) =>
                  setOfferForm({ ...offerForm, offer_price_label: e.target.value })
                }
                fullWidth
              />
              <TextField
                label="Available timing"
                placeholder="Today 4–6 PM"
                value={offerForm.available_timing}
                onChange={(e) =>
                  setOfferForm({ ...offerForm, available_timing: e.target.value })
                }
                fullWidth
              />
              <TextField
                label="Message"
                multiline
                rows={3}
                value={offerForm.message}
                onChange={(e) => setOfferForm({ ...offerForm, message: e.target.value })}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelected(null)}>Cancel</Button>
            <Button variant="contained" disabled={sending} onClick={sendOffer}>
              Send offer
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default CitizenRequirementsInbox;
